import { defineStore } from 'pinia'
import { Permission } from '../permissions'
import { AuthApi } from '../services/auth'
import { resetActivePackage } from '../composables/usePackageGate'

type AuthPayload = {
  userId: string
  tenantId: string
  email?: string
  fullName?: string
  businessName?: string
  logoUrl?: string
  startsAtUtc?: string
  endsAtUtc?: string
  packageName?: string
  permissions?: Permission[]
  roles?: string[]
  expiresAtUtc?: string
  isTenantAdmin?: boolean
}

let renewTimer: number | null = null

function clearRenewTimer() {
  if (renewTimer) {
    window.clearTimeout(renewTimer)
    renewTimer = null
  }
}

function scheduleRenew(expiresAtUtc?: string) {
  clearRenewTimer()
  if (!expiresAtUtc) return

  const now = Date.now()
  const exp = new Date(expiresAtUtc).getTime()
  const offsetMs = Math.max(5_000, exp - now - 60_000)

  renewTimer = window.setTimeout(async () => {
    const ok = await useUserStore().renewSilently()
    if (!ok) {
      window.location.href = '/auth/login?reauth=1'
    }
  }, offsetMs)
}

export const useUserStore = defineStore('user', {
  state: () => ({
    loaded: false,
    userId: '' as string,
    tenantId: '' as string,
    email: '' as string,
    fullName: '' as string,
    businessName: '' as string,
    logoPath: '' as string,
    startsAtUtc: '' as string,
    endsAtUtc: '' as string,
    packageName: '' as string,
    permissions: [] as Permission[],
    roles: [] as string[],
    expiresAtUtc: '' as string,
    isTenantAdmin: false as boolean,
  }),
  getters: {
    isAuthenticated: (s) => !!s.email && !!s.tenantId,
    userId: (s) => s.userId,
    has: (s) => (p: Permission) => s.permissions?.includes(p),
    hasRole: (s) => (r: string) => s.roles.includes(r),
    hasAny: (s) => (arr: Permission[]) => arr.some((p) => s.permissions?.includes(p)),
    hasAll: (s) => (arr: Permission[]) => arr.every((p) => s.permissions?.includes(p)),
    initials: (s) => {
      const name = s.businessName?.trim() || ''
      if (!name) return '?'
      return name
        .split(/\s+/)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('')
        .slice(0, 2)
    },
    planStartDate: (s) => (s.startsAtUtc ? new Date(s.startsAtUtc) : null),
    planEndDate: (s) => (s.endsAtUtc ? new Date(s.endsAtUtc) : null),
    isPlanExpired: (s) => (s.endsAtUtc ? new Date(s.endsAtUtc) < new Date() : false),
  },
  actions: {
    setFromInit(p: AuthPayload) {
      this.userId = p.userId
      this.tenantId = p.tenantId
      this.email = p.email as string
      this.fullName = p.fullName as string
      this.businessName = p.businessName as string
      this.logoPath = p.logoUrl as string
      this.startsAtUtc = p.startsAtUtc as string
      this.endsAtUtc = p.endsAtUtc as string
      this.packageName = p.packageName as string
      this.permissions = [...(p.permissions ?? [])]
      this.roles = [...(p.roles ?? [])]
      this.expiresAtUtc = p.expiresAtUtc as string
      this.isTenantAdmin = p.isTenantAdmin as boolean

      scheduleRenew(this.expiresAtUtc)
    },
    async fetchInitUserInfo() {
      try {
        if (this.loaded) return true
        const { data } = await AuthApi.InitUserInfo()
        this.setFromInit(data)
        this.loaded = true
        return true
      } catch {
        this.clear()
        this.loaded = false
        return false
      }
    },
    async ensureInitialized() {
      if (!this.loaded) {
        await this.fetchInitUserInfo()
      }
    },
    async renewSilently() {
      try {
        const { data } = await AuthApi.reissue()
        if (data?.permissions) this.permissions = data.permissions
        if (data?.roles) this.roles = data.roles
        if (data?.expiresAtUtc) this.expiresAtUtc = data.expiresAtUtc
        this.loaded = false
        await this.fetchInitUserInfo()
        return true
      } catch {
        this.clear()
        return false
      }
    },
    clear() {
      this.loaded = false
      this.userId = ''
      this.tenantId = ''
      this.email = ''
      this.fullName = ''
      this.businessName = ''
      this.logoPath = ''
      this.startsAtUtc = ''
      this.endsAtUtc = ''
      this.packageName = ''
      this.permissions = []
      this.roles = []
      this.expiresAtUtc = ''
      this.isTenantAdmin = false
      clearRenewTimer()
      resetActivePackage()
    },
    changeUserName(userName: string) {
      this.businessName = userName
    },
    setLogoPath(path: string) {
      this.logoPath = path
    },
    setPlanStart(iso: string) {
      this.startsAtUtc = iso
    },
    setPlanEnd(iso: string) {
      this.endsAtUtc = iso
    },
    setPermissions(perms: Permission[]) {
      this.permissions = [...(perms ?? [])]
    },
    setRoles(roles: string[]) {
      this.roles = [...(roles ?? [])]
    },
  },
})
